import React, { useRef, useState } from 'react';
import { Activity, ArrowRight, Dna, Watch } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackProductEvent } from '@/lib/analytics';
import { submitFeatureInterest } from '@/utils/submitFeatureInterest';
import base from '@/pages/platform/PersonalisedNutritionPage.module.css';
import styles from './RoadmapEarlyAccess.module.css';

const ROADMAP_OPTIONS = [
  { id: 'cgm', label: 'CGM integration', icon: Activity, eventName: 'PNP CGM Early Access Clicked' },
  { id: 'wearables', label: 'Wearable and activity data', icon: Watch, eventName: 'PNP Wearables Early Access Clicked' },
  { id: 'genetics_microbiome', label: 'Genetic and microbiome reports', icon: Dna, eventName: 'PNP Genetics and Microbiome Early Access Clicked' },
];

const RoadmapEarlyAccess: React.FC = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const scriptUrl = import.meta.env.VITE_FEATURE_INTEREST_SCRIPT_URL?.trim();
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const submission = useRef<{ key: string; id: string } | null>(null);
  const saving = useRef(false);
  const selectedOptions = ROADMAP_OPTIONS.filter(option => selectedIds.includes(option.id));
  const emailBody = `I'd like early access to the following CalorieScience features:\n\n${selectedOptions.map(option => `- ${option.label}`).join('\n')}`;
  const emailHref = `mailto:info@caloriescience.ai?subject=${encodeURIComponent('CalorieScience early access')}&body=${encodeURIComponent(emailBody)}`;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving.current || !scriptUrl || !selectedIds.length) return;
    saving.current = true;
    setStatus('saving');
    const key = JSON.stringify([email.trim(), [...selectedIds].sort()]);
    try {
      if (submission.current?.key !== key) submission.current = { key, id: crypto.randomUUID() };
      await submitFeatureInterest(scriptUrl, {
        email: email.trim(), features: selectedIds, requestId: submission.current.id, website,
      });
      setStatus('saved');
      trackProductEvent('Roadmap Interest Submitted', { features: selectedIds, element_section: 'nutrition_software_comparison_roadmap' });
    } catch {
      setStatus('error');
    } finally {
      saving.current = false;
    }
  };

  return (
    <section className={base.section} id="roadmap" aria-labelledby="pnp-roadmap-heading">
      <div className={base.inner}>
        <div className={base.roadmapPanel}>
          <div className={base.roadmapIntro}>
            <span className={base.kicker}>On the horizon</span>
            <h2 className={base.sectionTitle} id="pnp-roadmap-heading">Coming next to CalorieScience</h2>
            <p className={base.sectionText}>Tell us which features matter to your practice. We’ll let you know when they’re ready.</p>
            <ArrowRight className={base.roadmapArrow} size={40} strokeWidth={1.2} aria-hidden="true" />
          </div>
          <form className={base.roadmapForm} onSubmit={handleSubmit} aria-busy={status === 'saving'}>
            {status === 'saved' ? (
              <div className={styles.success} role="status" tabIndex={-1}>
                <h3>You’re on the list.</h3>
                <p>Your interests have been saved. We’ll email you when there’s news about your selected features.</p>
              </div>
            ) : <>

            <fieldset disabled={status === 'saving'} className={base.roadmapOptions} aria-describedby={scriptUrl ? undefined : "pnp-roadmap-help"}>
              <legend>Select the features you’re interested in</legend>
              {ROADMAP_OPTIONS.map(({ icon: Icon, ...option }) => (
                <label className={base.roadmapOption} key={option.id}>
                  <input
                    type="checkbox"
                    name="roadmap_interest"
                    value={option.id}
                    checked={selectedIds.includes(option.id)}
                    onChange={event => {
                      const selected = event.target.checked;
                      setSelectedIds(previous => selected
                        ? [...previous, option.id]
                        : previous.filter(id => id !== option.id));
                      trackProductEvent(option.eventName, {
                        element_section: 'nutrition_software_comparison_roadmap',
                        roadmap_feature: option.id,
                        action: 'toggle_interest',
                        selected,
                      });
                    }}
                  />
                  <Icon size={21} strokeWidth={1.6} aria-hidden="true" />
                  <span>{option.label}</span>
                </label>
              ))}
            </fieldset>
            {scriptUrl ? <>
              <label className={styles.emailLabel} htmlFor="roadmap-email">Email address</label>
              <input className={styles.emailInput} id="roadmap-email" type="email" name="email" autoComplete="email" required maxLength={254} value={email} onChange={event => setEmail(event.target.value)} disabled={status === 'saving'} placeholder="you@example.com" />
              <div className={styles.honeypot} aria-hidden="true">
                <label htmlFor="roadmap-website">Website</label>
                <input id="roadmap-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={event => setWebsite(event.target.value)} />
              </div>
              <button className={`${base.primaryButton} ${styles.submit}`} type="submit" disabled={!selectedIds.length || status === 'saving'}>
                {status === 'saving' ? 'Saving…' : 'Register my interest'} <ArrowRight size={17} aria-hidden="true" />
              </button>
              {status === 'error' && <p className={styles.error} role="alert">We couldn’t confirm your submission. Please try again, or <a href={emailHref}>email us your interests</a>.</p>}
              <p className={base.roadmapHelp}>By submitting, you agree to receive updates about these features. <Link to="/privacy">Privacy policy</Link></p>
            </> : <>
              <a className={base.primaryButton} href={selectedOptions.length ? emailHref : undefined} role="link" aria-disabled={!selectedOptions.length} tabIndex={0} onClick={event => {
                if (!selectedOptions.length) { event.preventDefault(); return; }
                selectedOptions.forEach(option => trackProductEvent(option.eventName, {
                  element_section: 'nutrition_software_comparison_roadmap', roadmap_feature: option.id,
                  action: 'request_early_access', selected: true,
                }));
              }}>
                Get early access <ArrowRight size={17} aria-hidden="true" />
              </a>
              <p className={base.roadmapHelp} id="pnp-roadmap-help">Select at least one feature. Opens an email to our team with your interests.</p>
            </>}
            </>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default RoadmapEarlyAccess;
